import { Stack, styled } from "~/common/mui";
import { useCallback, useRef, useState } from "react";
import { useDebounceCallback, useResizeObserver } from "usehooks-ts";

import type { CMSChipListDTO } from "~/features/cms/types/dto";
import { CMSChipLink } from "~/features/cms/components/cmsChip/CMSChipLink";
import DraggableContainer from "~/features/cms/components/cmsChip/DraggableContainer";

interface Props extends Omit<CMSChipListDTO, "type"> {
  className?: string;
}

export const CMSChipList = (props: Props) => {
  const { className } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const [centered, setCentered] = useState(false);

  const updateStyle = useCallback(() => {
    if (stackRef.current && containerRef.current) {
      const stackWidth = stackRef.current.offsetWidth;
      const containerWidth = containerRef.current.offsetWidth;
      setCentered(stackWidth < containerWidth);
    }
  }, []);

  const debouncedUpdateStyle = useDebounceCallback(updateStyle, 200);

  useResizeObserver({
    ref: containerRef,
    onResize: debouncedUpdateStyle,
  });

  return (
    <Container className={className} ref={containerRef} centered={centered}>
      <DraggableContainer>
        <Stack direction={"row"} gap={2} ref={stackRef}>
          {props.chipsComponent.map((dto, index) => (
            <CMSChipLink key={index} {...dto} variant="assist" />
          ))}
        </Stack>
      </DraggableContainer>
    </Container>
  );
};

const Container = styled("div", { shouldForwardProp: (prop) => prop !== "centered" })<{
  centered: boolean;
}>(({ centered }) => ({
  justifyContent: centered ? "center" : "flex-start",
  marginBottom: "-40px", // SCHAEFF-37473
}));
